/**
 * Servicio para Solicitudes de Margen Bruto Personalizado (/SolicitudMargenBruto).
 *
 * Flujo de estados de la solicitud:
 *   Pendiente → (tomar) → EnProceso → (completar y enviar) → Confirmada
 *
 * El adjunto del informe se sube en dos pasos:
 *   1. POST /Informe  → obtiene URL SAS de Azure Blob Storage
 *   2. PUT directo al blob usando esa URL
 */
import { BASE_URL } from "@/constants/endpoints";
import { createAxiosWithAuth } from "@/utils/axios-config";
import { objectToQueryStringWithArray } from "@/utils/query-string";
import { AxiosInstance } from "axios";

// Singleton: reutiliza la instancia autenticada en toda la sesión.
let grossMarginRequestsApiPromise: Promise<AxiosInstance> | null = null;

const getGrossMarginRequestsApi = async (): Promise<AxiosInstance | null> => {
  if (grossMarginRequestsApiPromise !== null) return grossMarginRequestsApiPromise;
  grossMarginRequestsApiPromise = createAxiosWithAuth(BASE_URL, "jwt");
  return grossMarginRequestsApiPromise;
};

export const GROSS_MARGIN_REQUESTS_BASE_PATH = "/SolicitudMargenBruto";

// ---- List ----

export interface GetGrossMarginRequestsProps {
  "P.Pagina"?: number;
  "P.Records"?: number;
  Cuenta?: number;
  Cultivos?: number[];
  Estados?: number[];
  Campanas?: number[];
  Fecha?: string;
}

export interface GrossMarginRequestListItem {
  id: number;
  fecha: string;
  titularCuenta: string;
  cultivos: string[];
  campanaProductiva: string;
  procedencia: string;
  estado: string;
}

export interface GrossMarginRequestsListResponse {
  listado: GrossMarginRequestListItem[];
  totalPages: number;
  totalRecords: number;
}

export const getGrossMarginRequests = async (
  props: GetGrossMarginRequestsProps = {},
): Promise<GrossMarginRequestsListResponse | null> => {
  const api = await getGrossMarginRequestsApi();
  if (!api) return null;

  const queryString = objectToQueryStringWithArray(props);
  const url = queryString
    ? `${GROSS_MARGIN_REQUESTS_BASE_PATH}?${queryString}`
    : GROSS_MARGIN_REQUESTS_BASE_PATH;
  const response = await api.get(url);
  return response.data;
};

// ---- Detail ----

export interface GranoProductivo {
  id: number;
  nombre: string;
  aplicaPPMB: boolean;
  sumaHA: boolean;
  precioPizarra: number;
}

export interface GrossMarginRequestCultivo {
  granoProductivo: GranoProductivo;
  rindeIndicado: number;
  rindeEsperado: number;
  cotizacionDolar: number;
  precioCereal: number;
  precioAgroquimicos: number;
  precioFertilizantes: number;
  precioSemillas: number;
  precioLabores: number;
  precioSeguros: number;
  precioAlquiler: number;
  gastoDeCosecha: number;
  inversionTotal: number;
  rentabilidad: number;
  ganancia: number;
  id: number
}

export interface GrossMarginRequestDetail {
  id: number;
  estado: string;
  titularCuenta: string;
  zonaProductiva: string;
  idZonaProductiva: number;
  idCampProductiva: number;
  procedencia: string;
  hectareas: number;
  tipoCampo: string;
  observacionProductor: string;
  cultivos: GrossMarginRequestCultivo[];
  /** Informe adjunto — solo presente cuando la solicitud está Confirmada. */
  nombreInforme?: string
  informeDisponible?: boolean
  urlInforme?: string
  notasInforme?: string
  sucursal?: string
}

export const getGrossMarginRequestDetail = async (
  id: number,
): Promise<GrossMarginRequestDetail | null> => {
  const api = await getGrossMarginRequestsApi();
  if (!api) return null;

  const response = await api.get(`${GROSS_MARGIN_REQUESTS_BASE_PATH}/${id}`);
  return response.data;
};

// ---- Update ----

export interface UpdateCultivoDto {
  id: number;
  cotizacionDolar: number;
  precioCereal: number;
  rindeEsperado: number;
  precioAgroquimicos: number;
  precioFertilizantes: number;
  precioSemillas: number;
  precioLabores: number;
  precioSeguros: number;
  costoImplantacion: number;
  precioAlquiler: number;
  gastoCosecha: number;
  inversionTotal: number;
  rentabilidad: number;
  ganancia: number;
}

export interface PatchGrossMarginRequestBody {
  idEstado?: number;
  urlInforme?: string;
  notasInforme?: string;
  cultivos?: UpdateCultivoDto[];
}

export const patchGrossMarginRequest = async (
  id: number,
  body: PatchGrossMarginRequestBody,
): Promise<string | null> => {
  const api = await getGrossMarginRequestsApi();
  if (!api) return null;

  const response = await api.patch(`${GROSS_MARGIN_REQUESTS_BASE_PATH}/${id}`, body);
  return response.data;
};

// ---- Informe ----

export interface InformeUploadResponse {
  fileId: string;
  /** URL SAS de Azure Blob Storage para subir el archivo directamente. */
  uploadUrl: string;
  fileUrl: string;
  idInformeMargenBruto: number;
}

export interface PostInformeBody {
  idSolicitud: number;
  tipoArchivo: string;
  nombreDocumento?: string
}

/** Registra un informe nuevo y devuelve la URL SAS para subirlo. */
export const postInformeMargenBruto = async (
  body: PostInformeBody,
): Promise<InformeUploadResponse | null> => {
  const api = await getGrossMarginRequestsApi();
  if (!api) return null;
  const response = await api.post(`${GROSS_MARGIN_REQUESTS_BASE_PATH}/Informe`, body);
  return response.data;
};

export interface PutInformeBody {
  idSolicitud: number;
  idInformeMargenBruto: number;
  tipoArchivo: string;
  nombreDocumento?: string;
}

/** Reemplaza un informe existente (mismo idInformeMargenBruto) y devuelve nueva URL SAS. */
export const putInformeMargenBruto = async (
  body: PutInformeBody,
): Promise<InformeUploadResponse | null> => {
  const api = await getGrossMarginRequestsApi();
  if (!api) return null;
  const response = await api.put(`${GROSS_MARGIN_REQUESTS_BASE_PATH}/Informe`, body);
  return response.data;
};
